import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "./axiosInstance"; // Axios instance with baseURL
import "./Premium.css";

// Utility to dynamically load Razorpay script
function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(true);

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;

    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error("Failed to load Razorpay SDK"));

    document.body.appendChild(script);
  });
}

function Premium({ onClose }) {
  const [loading, setLoading] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch logged-in email on mount
  useEffect(() => {
    const fetchEmail = async () => {
      try {
        const userId = localStorage.getItem("user_id"); // ✅ match SignInPage key
        if (!userId) throw new Error("User ID missing. Please login again.");

        const res = await api.get(`/get-user-email/${userId}`);
        if (res.data?.email) {
          setUserEmail(res.data.email);
          console.log("Fetched email:", res.data.email);
        } else {
          alert("Unable to fetch your email. Please login again.");
          navigate("/signin");
        }
      } catch (err) {
        console.error("Failed to fetch email:", err);
        alert("Failed to fetch email. Please login again.");
        navigate("/signin");
      } finally {
        setEmailLoading(false);
      }
    };

    fetchEmail();
  }, [navigate]);

  // Main payment flow
  const handlePayment = async () => {
    if (!userEmail) {
      alert("Email not available yet. Please wait a moment.");
      return;
    }

    setLoading(true);

    try {
      // 1️⃣ Get Razorpay key from backend
      const keyResp = await api.get("/get-razorpay-key");
      const RAZORPAY_KEY_ID = keyResp.data.key;
      if (!RAZORPAY_KEY_ID) throw new Error("Razorpay key not found");

      // 2️⃣ Create Razorpay order
      const amountINR = 199;
      const amountPaisa = amountINR * 100;
      const orderResp = await api.post("/create-razorpay-order", {
        amount: amountPaisa,
        currency: "INR",
        receipt: `receipt_${Date.now()}`,
        notes: { email: userEmail },
      });

      const { order_id, amount, currency } = orderResp.data;

      // 3️⃣ Load Razorpay SDK
      await loadRazorpayScript();
      if (!window.Razorpay) throw new Error("Razorpay SDK not available");

      // 4️⃣ Configure Razorpay checkout
      const options = {
        key: RAZORPAY_KEY_ID,
        amount,
        currency: currency || "INR",
        name: "Stratxcel",
        description: "Buy Credits / Premium Plan",
        order_id,
        prefill: { email: userEmail },
        handler: async (response) => {
          try {
            const verifyResp = await api.post("/verify-payment", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (verifyResp.data.status === "success") {
              alert("✅ Payment successful! Credits added.");
              if (typeof onClose === "function") onClose({ paid: true });
            } else {
              alert("❌ Payment verification failed.");
            }
          } catch (err) {
            console.error("Verification error:", err);
            alert("Server verification failed. Please contact support.");
          }
        },
        modal: {
          ondismiss: () => {
            if (typeof onClose === "function")
              onClose({ paid: false, dismissed: true });
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        console.error("Payment failed:", response.error);
        alert("Payment failed. Please try again.");
      });
      rzp.open();
    } catch (err) {
      console.error("Payment flow error:", err);
      alert("Error starting payment: " + (err.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="premium-overlay">
      <div className="premium-popup">
        <div className="premium-grid">
          {/* === LEFT COLUMN === */}
          <div className="premium-col left-col">
            <h1>Turn Your Business Idea into Reality – Step by Step</h1>
            <p>
              From registration to sales, <b>Stratxcel.ai</b> handles the hard
              work while you focus on your vision.
            </p>
            <button
              className="premium-btn"
              onClick={handlePayment}
              disabled={loading || emailLoading}
            >
              {loading
                ? "Processing..."
                : emailLoading
                ? "Loading..."
                : "Start My Business Journey"}
            </button>

            {/* Steps */}
            {[
              {
                title: "Business Idea & Project Analysis",
                desc: "Validate your idea with market research, feasibility checks, and cost-profit estimates.",
                btn: "Get Free Business Analysis",
              },
              {
                title: "Legal Foundation & Company Registration",
                desc: "Pvt Ltd, LLP, OPC, GST, PAN/TAN – all handled in one click.",
                btn: "Register My Company Now",
              },
              {
                title: "Team & People Setup",
                desc: "Hire employees, manage compliance, and set up payroll effortlessly.",
                btn: "Build My Team",
              },
              {
                title: "Digital & Physical Presence",
                desc: "Get your website, e-commerce store, or shop setup ready to launch.",
                btn: "Launch My Website/Shop",
              },
              {
                title: "Branding & Marketing",
                desc: "Logo, brand story, and social media presence – everything for a powerful identity.",
                btn: "Create My Brand Identity",
              },
              {
                title: "Sales & Growth Strategy",
                desc: "Sales funnels, CRM setup, and lead management to boost your growth.",
                btn: "Boost My Sales",
              },
              {
                title: "AI & Automation for Business",
                desc: "Smart chatbots, workflow automation, and AI-powered operations.",
                btn: "Automate My Business",
              },
            ].map((step, idx) => (
              <div className="step" key={idx}>
                <h3>{`${idx + 1}️⃣ ${step.title}`}</h3>
                <p>{step.desc}</p>
                <button className="step-btn">{step.btn}</button>
              </div>
            ))}

            <h2>Why Choose Premium?</h2>
            <ul>
              <li>
                Done-for-You Setup – Registration, branding & website in one
                click.
              </li>
              <li>AI Co-Pilot – Step-by-step guidance like a mentor.</li>
              <li>One Dashboard – Everything in one place.</li>
              <li>Hybrid Support – Human experts + AI automation.</li>
            </ul>

            <button
              className="premium-btn"
              onClick={handlePayment}
              disabled={loading || emailLoading}
            >
              {loading
                ? "Processing..."
                : emailLoading
                ? "Loading..."
                : "Launch My Business Today"}
            </button>
            <br />
            <button className="premium-close-btn" onClick={onClose}>
              Close
            </button>
          </div>

          {/* === RIGHT COLUMN (Pricing Plans) === */}
          <div className="premium-col right-col">
            <h2>Pricing & Plans</h2>
            <div className="pricing-box">
              <h3>Free Plan</h3>
              <p>Basic guidance to get you started.</p>
              <button className="plan-btn">Try Free</button>
            </div>

            <div className="pricing-box premium-plan">
              <h3>Premium Plan</h3>
              <p>
                <b>All-in-one launch package</b> – from idea to business running
                live.
              </p>
              <p className="price">Only ₹199 – One-time Complete Package</p>
              <button
                className="plan-btn"
                onClick={handlePayment}
                disabled={loading || emailLoading}
              >
                {loading
                  ? "Processing..."
                  : emailLoading
                  ? "Loading..."
                  : "Get Started Now"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Premium;
